/**
 * Ads Manager main view — white container with Campaign Details + Ad Groups.
 * Each ad group renders in its own rounded #f8f9fa card, mirroring the
 * Product Strategies visual pattern from CampaignDetailsView.
 */
import React, { useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import MeridianLoader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import refreshTokens from '@amzn/meridian-tokens/base/icon/refresh';
import { useTranslation } from 'src/arb-wrapper';
import { Loader } from 'src/components/common/Loader';
import type {
  PublisherEntitiesControllerListForCampaignApiResponse,
  PublisherEntityDto,
} from 'src/store/generated/leela-api-generated';
import { usePublisherEntitiesControllerListForCampaignQuery } from 'src/store/leela-api';
import { narrowCampaignMetadata } from 'src/utils/publisherEntityMetadata';
import { EntityAssetStrip } from './EntityAssetStrip';
import { EntityGroupRow } from './EntityGroupRow';
import { EntityIcon } from './EntityIcon';
import { EntityChildTable } from './EntityChildTable';
import { EntityMetadataDetails } from './EntityMetadataDetails';
import {
  ADS_STATUS_FILTER_PARAMS,
  type AdsStatusFilter,
} from './statusFilterParams';
import {
  BADGE_LABELS,
  derivePublisherEntityStatus,
} from 'src/utils/publisherEntityStatus';
import Tag from '@amzn/meridian/tag';
import { EMPTY_VALUE, formatUpdatedAt } from './StatusBadge';

const STATUS_TAG_TYPES: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  LIVE: 'success',
  PENDING: 'warning',
  FAILED: 'error',
  SKIPPED: 'neutral',
  REMOVED: 'neutral',
};

export interface EntitiesQueryResult {
  data?: PublisherEntitiesControllerListForCampaignApiResponse;
  isError: boolean;
  isLoading: boolean;
}

interface EntitiesViewProps {
  adGroupsQuery: EntitiesQueryResult;
  campaignId: string;
  campaignQuery: EntitiesQueryResult;
  isFetching?: boolean;
  onRefresh?: () => void;
}

type EntityViewType = 'AD_GROUP' | 'AD' | 'ASSET';

export const EntitiesView: React.FC<EntitiesViewProps> = ({
  adGroupsQuery,
  campaignId,
  campaignQuery,
  isFetching,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [entityView, setEntityView] = useState<EntityViewType>('AD_GROUP');
  const [adsPage, setAdsPage] = useState(1);
  const [adsFilter, setAdsFilter] = useState<AdsStatusFilter>('LIVE');
  const [selectedAdId, setSelectedAdId] = useState<string | undefined>();
  const campaignEntities = (campaignQuery.data?.data ?? []) as PublisherEntityDto[];
  const adGroups = (adGroupsQuery.data?.data ?? []) as PublisherEntityDto[];
  const adGroupTotal = adGroupsQuery.data?.meta.pagination.total ?? adGroups.length;
  const hasQueryError = campaignQuery.isError || adGroupsQuery.isError;
  const isLedgerEmpty = !hasQueryError && campaignEntities.length === 0 && adGroups.length === 0;
  const campaignEntity = campaignEntities[0];
  const campaignMeta = campaignEntity ? narrowCampaignMetadata(campaignEntity.publisherMetadata) : undefined;

  // All-ads query (only active when viewing Ads)
  const allAdsQuery = usePublisherEntitiesControllerListForCampaignQuery({
    campaignId,
    page: adsPage,
    pageSize: 25,
    publisher: 'google',
    publisherEntityType: 'AD',
    ...ADS_STATUS_FILTER_PARAMS[adsFilter],
  }, { skip: entityView !== 'AD' });

  const handleEntityViewChange = (value: EntityViewType) => {
    setEntityView(value);
    setAdsPage(1);
    setAdsFilter('LIVE');
    setSelectedAdId(undefined);
  };

  const handleAdsFilterChange = (value: AdsStatusFilter) => {
    setAdsFilter(value);
    setAdsPage(1);
  };

  return (
    <Loader isLoading={campaignQuery.isLoading || adGroupsQuery.isLoading}>
      <Box spacingInset="500" type="outline">
      <Column spacing="600">
        {/* Campaign Details Section — white background */}
        <Column spacing="400">
          <Row alignmentHorizontal="justify" alignmentVertical="center">
            <Text type="h200">{t('ads_manager_campaign_details_header')}</Text>
            {onRefresh != null && (
              <Row alignmentVertical="center" spacing="300">
                {isFetching && <MeridianLoader size="small" type="linear" />}
                <Column alignmentHorizontal="center" spacing="100">
                  <Button
                    data-testid="ads-manager-refresh-button"
                    disabled={isFetching}
                    size="small"
                    type="icon"
                    onClick={onRefresh}
                  >
                    <Icon tokens={refreshTokens} />
                  </Button>
                  <Text color="secondary" type="b100">{t('ads_manager_refresh')}</Text>
                </Column>
              </Row>
            )}
          </Row>
          <Divider />

          {hasQueryError && (
            <Alert data-testid="ads-manager-entities-query-error" size="small" type="error">
              {t('ads_manager_query_error')}
            </Alert>
          )}

          {isLedgerEmpty
            ? (
              <Text color="secondary" data-testid="ads-manager-empty-ledger">
                {t('ads_manager_empty_ledger')}
              </Text>
              )
            : (
              <Column spacing="300">
                {campaignEntity != null && (
                  <Column spacing="200">
                    <Row alignmentVertical="center" spacing="300">
                      <EntityIcon size={32} type={campaignEntity.publisherEntityType} />
                      <Text type="b300">{campaignMeta?.name ?? EMPTY_VALUE}</Text>
                    </Row>
                    <Row spacing="200" widths={['240px', 'fill']}>
                      <Text color="secondary" type="b300">{t('ads_manager_col_entity_id')}</Text>
                      <Text type="b300">{campaignEntity.publisherEntityId ?? EMPTY_VALUE}</Text>
                    </Row>
                    <Row spacing="200" widths={['240px', 'auto']}>
                      <Text color="secondary" type="b300">{t('ads_manager_col_status')}</Text>
                      <Tag type={STATUS_TAG_TYPES[derivePublisherEntityStatus(campaignEntity)]}>{t(BADGE_LABELS[derivePublisherEntityStatus(campaignEntity)])}</Tag>
                    </Row>
                    <Row spacing="200" widths={['240px', 'fill']}>
                      <Text color="secondary" type="b300">{t('ads_manager_col_last_updated')}</Text>
                      <Text type="b300">{formatUpdatedAt(campaignEntity.updatedAt)}</Text>
                    </Row>
                    {campaignEntity.lastError != null && (
                      <Row spacing="200" widths={['240px', 'fill']}>
                        <Text color="secondary" type="b300">{t('ads_manager_col_last_error')}</Text>
                        <Text color="error" type="b300">{campaignEntity.lastError}</Text>
                      </Row>
                    )}
                    <Link
                      data-testid="ads-manager-campaign-show-details"
                      type="secondary"
                      onClick={() => { setShowDetails(prev => !prev); }}
                    >
                      {t('ads_manager_details')}
                    </Link>
                  </Column>
                )}

              </Column>
              )}

          {showDetails && campaignEntity != null && (
            <EntityMetadataDetails
              layout="row"
              metadata={(() => {
                const meta = campaignEntity.publisherMetadata as Record<string, unknown> | undefined;
                if (!meta) return undefined;
                const { name: _name, ...rest } = meta;
                const filtered = Object.fromEntries(
                  Object.entries(rest).filter(([, v]) => v !== undefined && v !== null),
                );
                return Object.keys(filtered).length > 0 ? filtered : undefined;
              })()}
              testId="ads-manager-campaign-metadata"
            />
          )}
        </Column>

        {/* Entity View Section — switchable between Ad Groups / Ads / Assets */}
        {!isLedgerEmpty && (
        <Column spacing="400">
          <Row alignmentVertical="center" spacing="300">
            <Select
              data-testid="ads-manager-entity-view-filter"
              label={t('ads_manager_filter_type')}
              size="xlarge"
              value={entityView}
              width={200}
              onChange={handleEntityViewChange}
            >
              <SelectOption label={t('ads_manager_ad_groups_header')} value="AD_GROUP" />
              <SelectOption label={t('ads_manager_ads_header')} value="AD" />
              <SelectOption label={t('ads_manager_assets_header')} value="ASSET" />
            </Select>
          </Row>
          <Divider />

          {/* Ad Groups View */}
          {entityView === 'AD_GROUP' && (
            <>
              {adGroups.length === 0 && !isLedgerEmpty && (
                <Text color="secondary">{t('ads_manager_no_ad_groups')}</Text>
              )}
              <Column spacing="300">
                {adGroups.map(entity => (
                  <div key={entity._id} style={{ borderRadius: '8px', overflow: 'hidden' }}>
                    <Box backgroundColor="subdued" spacingInset="400">
                      <EntityGroupRow
                        campaignId={campaignId}
                        entity={entity}
                      />
                    </Box>
                  </div>
                ))}
              </Column>
              {adGroupTotal > adGroups.length && (
                <Text color="secondary" data-testid="ads-manager-ad-groups-total-notice">
                  {t('ads_manager_ad_groups_total_notice', {
                    loaded: adGroups.length,
                    total: adGroupTotal,
                  })}
                </Text>
              )}
            </>
          )}

          {/* Ads View — flat table of all ads across all groups */}
          {entityView === 'AD' && (
            <EntityChildTable
              campaignId={campaignId}
              data={(allAdsQuery.data?.data ?? []) as PublisherEntityDto[]}
              isError={allAdsQuery.isError}
              isFetching={allAdsQuery.isFetching}
              isLoading={allAdsQuery.isLoading}
              page={adsPage}
              pagination={allAdsQuery.data?.meta.pagination ?? { page: 1, pageCount: 1, pageSize: 25, total: 0 }}
              selectedAdId={selectedAdId}
              showDivider={false}
              statusFilter={adsFilter}
              onPageChange={setAdsPage}
              onSelectAd={(ad) => { setSelectedAdId(prev => prev === ad._id ? undefined : ad._id); }}
              onStatusFilterChange={handleAdsFilterChange}
            />
          )}

          {/* Assets View — horizontal scrollable asset cards */}
          {entityView === 'ASSET' && (
            <EntityAssetStrip
              adSourceEntityId={undefined}
              campaignId={campaignId}
            />
          )}
        </Column>
        )}
      </Column>
      </Box>
    </Loader>
  );
};
